<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useCryptoStore } from '@/store/modules/crypto.store'

const store = useCryptoStore()

// ------------------------------
// UI State
// ------------------------------
const selectedSymbol = ref<string | null>(null)

const sortBy = ref<'symbol' | 'rsr100' | 'rvol_m5' | 'rvol_h1' | 'rvol_day'>('rsr100')
const sortDir = ref<'asc' | 'desc'>('desc')

const sortArrow = computed(() => (sortDir.value === 'asc' ? '▲' : '▼'))

function setSort(column: typeof sortBy.value) {
  if (sortBy.value === column) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = column
    sortDir.value = 'desc'
  }
}

// ------------------------------
// Computed: Sorted results
// ------------------------------
const sorted = computed(() => {
  return [...store.results]
    .map(r => ({
      ...r,
      rsr100: r.rsr - 100,
    }))
    .sort((a, b) => {
      const dir = sortDir.value === 'asc' ? 1 : -1

      let A: any
      let B: any

      switch (sortBy.value) {
        case 'symbol':
          A = a.symbol
          B = b.symbol
          return A.localeCompare(B) * dir

        case 'rsr100':
          A = a.rsr100
          B = b.rsr100
          break

        case 'rvol_m5':
          A = a.volume?.rvol_m5 ?? 0
          B = b.volume?.rvol_m5 ?? 0
          break

        case 'rvol_h1':
          A = a.volume?.rvol_h1 ?? 0
          B = b.volume?.rvol_h1 ?? 0
          break

        case 'rvol_day':
          A = a.volume?.rvol_day ?? 0
          B = b.volume?.rvol_day ?? 0
          break
      }

      return (A - B) * dir
    })
})


// ------------------------------
// Helpers
// ------------------------------
function fmt(v: number) {
  return Number(v).toFixed(2)
}

function fmt3(v?: number) {
  if (v == null) return '-'
  return Number(v).toFixed(3)
}

// ------------------------------
// Symbol select (web version)
// ------------------------------
function selectSymbol(symbol: string) {
  selectedSymbol.value = symbol
  // WebApp phase: แค่ hook ไว้ก่อน
  console.log('[Crypto] selectSymbol:', symbol)
}

// ------------------------------
// Lifecycle
// ------------------------------
onMounted(() => {
  store.fetchRSR()
  store.startAutoRefresh()
})

onBeforeUnmount(() => {
  store.stopAutoRefresh()
})
</script>

<template>
  <v-card>
    <v-card-title>Crypto</v-card-title>

    <v-card-text>

      <!-- ======================
           Controls
      ====================== -->
      <div
        style="
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
          margin-bottom: 12px;
        "
      >
        <v-btn
          size="small"
          color="primary"
          variant="tonal"
          @click="store.fetchRSR"
        >
          Refresh
        </v-btn>

        <v-text-field
        v-model="store.t1_input"
        label="t1"
        density="compact"
        variant="outlined"
        hide-details
        class="mini-date"
        />

        <v-text-field
        v-model="store.t2_input"
        label="t2"
        density="compact"
        variant="outlined"
        hide-details
        :disabled="store.useNow"
        class="mini-date"
        />

        <v-switch
          v-model="store.useNow"
          label="now"
          color="primary"
          hide-details
          inset
        />
      </div>

      <!-- ======================
           Status
      ====================== -->
      <div v-if="store.isLoading" style="padding: 8px; color: #aaa">
        ⏳ Loading...
      </div>

      <div v-if="store.error" style="padding: 8px; color: #ef5350">
        {{ store.error }}
      </div>

      <!-- ======================
           Table
      ====================== -->
      <v-table density="compact">
        <thead>
          <tr>
            <th @click="setSort('symbol')" style="cursor:pointer">
              Symbol
              <span v-if="sortBy === 'symbol'">{{ sortArrow }}</span>
            </th>

            <th @click="setSort('rsr100')" style="cursor:pointer">
              RSR
              <span v-if="sortBy === 'rsr100'">{{ sortArrow }}</span>
            </th>

            <th @click="setSort('rvol_m5')" style="cursor:pointer">
              m5
              <span v-if="sortBy === 'rvol_m5'">{{ sortArrow }}</span>
            </th>

            <th @click="setSort('rvol_h1')" style="cursor:pointer">
              h1
              <span v-if="sortBy === 'rvol_h1'">{{ sortArrow }}</span>
            </th>

            <th @click="setSort('rvol_day')" style="cursor:pointer">
              d1
              <span v-if="sortBy === 'rvol_day'">{{ sortArrow }}</span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="r in sorted"
            :key="r.symbol"
            @click="selectSymbol(r.symbol)"
            :style="{
              cursor: 'pointer',
              background:
                selectedSymbol === r.symbol ? '#263238' : undefined
            }"
          >
            <td>{{ r.symbol }}</td>

            <td :style="{ color: r.rsr100 >= 0 ? '#43A047' : '#E53935' }">
              {{ fmt(r.rsr100) }}
            </td>

            <td>{{ fmt3(r.volume?.rvol_m5) }}</td>
            <td>{{ fmt3(r.volume?.rvol_h1) }}</td>
            <td>{{ fmt3(r.volume?.rvol_day) }}</td>
          </tr>
        </tbody>
      </v-table>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.mini-date {
  max-width: 150px;
}

/* ลดความสูงจริง */
.mini-date :deep(.v-field) {
  min-height: 30px;
}

.mini-date :deep(.v-field__input) {
  padding-top: 2px;
  padding-bottom: 2px;
  font-size: 12px;
  min-height: 28px;
}

/* label ให้เล็กลง */
.mini-date :deep(label) {
  font-size: 11px;
}

</style>